import React from 'react';
import { IconArrowRight } from '@tabler/icons-react';
import { Carousel } from '@mantine/carousel';

import crest from '@assets/images/tc-gymnasium-logo.png';
import foundersFistBump from '@assets/images/tc-founders-fist-bump.png';
import martialArtsMishap from '@assets/images/tc-founders-martial-arts-mishap.png';
import gameNight from '@assets/images/tc-founders-game-night.png';
import gameDay from '@assets/images/tc-founders-game-day.png';
import lacrosseDay from '@assets/images/tc-founders-lacrosse-day.png';
import tuckerPortrait from '@assets/images/tc-tucker-multi-sport-portrait.png';
import clarkPortrait from '@assets/images/tc-clark-junior-founder-portrait.png';
import tuckMascotPortrait from '@assets/images/tc-tuck-mascot-portrait.png';
import clarkMascotPortrait from '@assets/images/tc-clark-mascot-portrait.png';
import dooeyMascotPortrait from '@assets/images/tc-dooey-mascot-portrait.png';

import homeClasses from './Home.module.css';
import classes from './Founders.module.css';

const founders = [
  {
    name: 'Tucker',
    nickname: '“Tuck”',
    role: 'Co-founder · Senior athlete',
    portrait: tuckerPortrait,
    portraitAlt: 'Tucker holding a lacrosse stick',
    biography: [
      "Tucker helped establish T&C's Gymnasium on the radical belief that you can be both strong and nice.",
      'A lacrosse faceoff specialist, football long snapper, Tae Kwon Do black belt and recovering cross-country runner, Tuck is a hard worker, highly coachable and remarkably good at figuring things out—even when he has absolutely no idea where he put them.',
      'Away from the Gymnasium, his interests include history, saltwater fish, his friends, and maintaining considerably stronger opinions about clothes than most people who can squat 405 pounds.',
      'Thoughtful, dependable and genuinely kind, Tuck is the steady older brother Clarkie looks up to. He also possesses a sense of humor substantially darker than his official Gymnasium portrait would suggest.',
      "Give him a problem and he'll solve it. Give him five minutes to get ready and he'll need seven.",
    ],
    facts: [
      ['Age', '17'],
      ['Sports', 'Lacrosse (FOGO) · Football (Long Snapper) · Tae Kwon Do · Former XC'],
      ['Specialties', 'Faceoffs · Problem Solving · Heavy Things'],
      ['Interests', 'History · Saltwater Aquariums · Friends · Looking Good'],
      ['Known for', '405 Club · Being Coachable · Questionable Time Management'],
      ['Official status', 'Probably looking for something.'],
      ['Philosophy', '“Just do the work.”'],
    ],
    quote: '“Stronger today than yesterday.”',
    signature: '— Tuck',
  },
  {
    name: 'Clark',
    nickname: '“Clarkie”',
    role: 'Co-founder · Junior athlete',
    portrait: clarkPortrait,
    portraitAlt: 'Clark holding his weather notebook',
    biography: [
      "Clark helped establish T&C's Gymnasium at the age of nine and has never allowed being the younger brother to limit his involvement in management.",
      'Smart, tough, and equipped with an opinion on virtually everything, Clark plays lacrosse and trains in Tae Kwon Do. He follows the rules, remembers dates nobody else remembers, keeps a close eye on the weather, and generally knows exactly what time it is.',
      "Away from the Gymnasium, Clark can usually be found on Fortnite, Roblox, or his iPad; playing chess; or outside in the driveway turning whatever happens to be available into that day's activity. He also maintains that chocolate milk is an essential component of athletic development. The Gymnasium has chosen not to challenge this position.",
      'Clark is spicy, funny, clever, and considerably tougher than his size would suggest. His admiration for his big brother is enormous.',
      'This does not prevent him from correcting Tucker when Tucker is wrong.',
    ],
    facts: [
      ['Age', '9'],
      ['Sports', 'Lacrosse · Tae Kwon Do'],
      ['Specialties', 'Rules & Regulations · Weather · Dates & Times · Strong Opinions'],
      ['Interests', 'Fortnite · Roblox · Chess · Driveway Activities · Chocolate Milk'],
      ['Known for', 'Knowing What Time It Is · Having Thoughts · Being Tough'],
      ['Additional duties', 'Staff Meteorologist'],
      ['Official status', 'Aware of the time.'],
      ['Philosophy', "“Technically, you never said I couldn't.”"],
    ],
    quote: '“Smarter, stronger, faster, louder.”',
    signature: '— Clarkie',
  },
];

const founderGallery = [
  {
    image: martialArtsMishap,
    alt: 'Tucker watches as Clark applies Tae Kwon Do to a lacrosse stick',
    title: 'Cross-training',
    caption: 'The equipment budget remains under review.',
  },
  {
    image: gameNight,
    alt: 'Tucker and Clark playing video games together in their shared room',
    title: 'Recovery protocol',
    caption: 'Good games. Good friends. Better tomorrow.',
  },
  {
    image: tuckerPortrait,
    alt: 'Tucker with a lacrosse stick and football',
    title: 'The senior athlete',
    caption: 'Prepared for nearly every sport. Prepared on time is a separate matter.',
  },
  {
    image: gameDay,
    alt: 'Tucker long snapping while Clark serves as an enthusiastic ball boy',
    title: 'Game-day operations',
    caption: 'Senior long snapper. Junior sideline management.',
  },
  {
    image: clarkPortrait,
    alt: 'Clark seated with his lacrosse stick, tablet, and backpack',
    title: 'The junior athlete',
    caption: 'Rules observed. Time confirmed. Opinion available.',
  },
  {
    image: lacrosseDay,
    alt: 'Clark taking a lacrosse shot while Tucker cheers from the sideline',
    title: 'Big brother support',
    caption: 'Different roles. Same sideline.',
  },
];

function EnterTheGymLink({ className }) {
  return (
    <a className={className} href="/app">
      Enter the gym
      <IconArrowRight size={16} stroke={2.4} aria-hidden="true" />
    </a>
  );
}

function FounderCard({ founder }) {
  return (
    <article className={classes.founderCard}>
      <div className={classes.portraitWrap}>
        <img src={founder.portrait} alt={founder.portraitAlt} />
      </div>

      <div className={classes.founderDetails}>
        <header>
          <h2>{founder.name}</h2>
          <p className={classes.nickname}>{founder.nickname}</p>
          <p className={classes.role}>{founder.role}</p>
        </header>

        <div className={classes.biography}>
          {founder.biography.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>

        <dl className={classes.factList}>
          {founder.facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <div className={classes.founderHonor}>
          <blockquote>
            <p>{founder.quote}</p>
            <cite>{founder.signature}</cite>
          </blockquote>
        </div>
      </div>
    </article>
  );
}

export default function MarketingFounders() {
  return (
    <div className={homeClasses.page}>
      <header className={homeClasses.header}>
        <a className={homeClasses.brand} href="/" aria-label="T&C's Gymnasium home">
          <img src={crest} alt="" />
          <span>
            <strong>T&amp;C’s Gymnasium</strong>
            <small>Discipline builds freedom.</small>
          </span>
        </a>

        <nav className={homeClasses.nav} aria-label="Main navigation">
          <a className={homeClasses.navDesktopOnly} href="/">
            Home
          </a>
          <a href="/#gymnasium">The Gymnasium</a>
          <a className={homeClasses.navActive} href="/the-founders">
            The Founders
          </a>
          <a className={homeClasses.navDesktopOnly} href="/#record-book">
            Record Book
          </a>
        </nav>
      </header>

      <main>
        <section className={classes.foundersHero} aria-labelledby="founders-title">
          <div className={classes.heroCopy}>
            <p className={classes.eyebrow}>The founders</p>
            <h1 id="founders-title">
              Two brothers.
              <br />
              Eight years apart.
              <br />
              <span>One gymnasium.</span>
            </h1>
            <p className={classes.intro}>
              T&amp;C’s Gymnasium was founded by Tucker and Clark — two brothers
              with different perspectives, the same standards, and a shared
              commitment to getting better. Through training, discipline, and
              a lot of friendly competition, they’re building stronger
              athletes and kinder humans.
            </p>
            <p className={classes.values}>
              Strength <span>★</span> Character <span>★</span> Progress{' '}
              <span>★</span> Brotherhood
            </p>
          </div>

          <div className={classes.heroArtwork}>
            <img
              src={foundersFistBump}
              alt="Tucker and Clark sharing a fist bump in the gym"
            />
          </div>

          <ol className={classes.mantra} aria-label="Gymnasium method">
            <li>Work</li>
            <li>Train</li>
            <li>Learn</li>
            <li>Compete</li>
            <li>Improve</li>
            <li>Repeat</li>
          </ol>
        </section>

        <section className={classes.profileGrid} aria-label="Founder profiles">
          {founders.map((founder) => (
            <FounderCard founder={founder} key={founder.name} />
          ))}
        </section>

        <section className={classes.founderArchive} aria-labelledby="archive-title">
          <div className={classes.archiveHeading}>
            <p className={classes.eyebrow}>From the official files</p>
            <h2 id="archive-title">Founder archives</h2>
            <p>
              Selected evidence from the continuing history of T&amp;C
              Gymnasium.
            </p>
          </div>

          <Carousel
            classNames={{
              root: classes.archiveCarousel,
              viewport: classes.archiveViewport,
              control: classes.archiveControl,
              indicators: classes.archiveIndicators,
              indicator: classes.archiveIndicator,
            }}
            withIndicators
            loop
          >
            {founderGallery.map((item) => (
              <Carousel.Slide key={item.title}>
                <figure className={classes.archiveSlide}>
                  <img src={item.image} alt={item.alt} />
                  <figcaption>
                    <strong>{item.title}</strong>
                    <span>{item.caption}</span>
                  </figcaption>
                </figure>
              </Carousel.Slide>
            ))}
          </Carousel>
        </section>

        <section className={classes.brothersFirst}>
          <div className={classes.brotherhoodPanel}>
            <p>Brothers</p>
            <strong>First.</strong>
          </div>

          <div className={classes.story}>
            <p className={classes.eyebrow}>More than a gym</p>
            <h2>Brothers first.</h2>
            <p>
              Eight years separate Tuck and Clarkie, but sport has given them
              common ground. They train together, compete, give each other a
              hard time, and push each other to be better. T&amp;C’s Gymnasium
              exists to give them a place to work, grow, laugh, and keep track
              of what they accomplish along the way.
            </p>
            <a href="#founders-title">
              Our story
              <IconArrowRight size={14} stroke={2.4} aria-hidden="true" />
            </a>
          </div>

          <aside className={classes.hierarchy}>
            <p className={classes.eyebrow}>Official gymnasium hierarchy</p>
            <ol>
              <li>
                <span className={classes.hierarchyAvatar}>
                  <img src={tuckMascotPortrait} alt="" />
                </span>
                <div>
                  <strong>Tuck</strong>
                  <small>Senior athlete</small>
                </div>
              </li>
              <li>
                <span className={classes.hierarchyAvatar}>
                  <img src={clarkMascotPortrait} alt="" />
                </span>
                <div>
                  <strong>Clarkie</strong>
                  <small>Junior athlete</small>
                </div>
              </li>
              <li>
                <span className={classes.hierarchyAvatar}>
                  <img src={dooeyMascotPortrait} alt="" />
                </span>
                <div>
                  <strong>Dooey</strong>
                  <small>Facilities &amp; IT</small>
                </div>
              </li>
            </ol>
            <blockquote>
              “Love plus Compassion equals Strength”
              <cite>— Dooey</cite>
            </blockquote>
          </aside>
        </section>
      </main>

      <footer className={homeClasses.footer}>
        <a className={homeClasses.brand} href="/" aria-label="T&C's Gymnasium home">
          <img src={crest} alt="" />
          <span>
            <strong>T&amp;C’s Gymnasium</strong>
            <small>Discipline builds freedom.</small>
          </span>
        </a>
        <nav aria-label="Footer navigation">
          <a href="/">Home</a>
          <a href="/#gymnasium">The Gymnasium</a>
          <a href="/the-founders">The Founders</a>
          <a href="/#record-book">Record Book</a>
        </nav>
        <EnterTheGymLink className={homeClasses.footerCta} />
        <p className={homeClasses.location}>Est. 2026 · Brooks, GA</p>
        <p className={homeClasses.copyright}>
          © 2026 T&amp;C’s Gymnasium. All rights reserved.
        </p>
        <p className={homeClasses.footerMotto}>
          Stronger athletes. Kinder humans.
        </p>
      </footer>
    </div>
  );
}
